import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ComponentType,
  type FC,
} from "react";
import type { Dayjs } from "dayjs";
import ChartDataLabels from "chartjs-plugin-datalabels";

import DatePickerJs from "../../../shared/DatePicker/DatePicker";
import ExportToXlsxButtonJs from "../../../CommonComponents/ExportToXlsxButton/ExportToXlsxButton";
import { Select } from "../../../shared";
import { BarChart, CHART_PALETTE, ChartCard } from "../../../shared/Charts";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { DATE_RANGE } from "../../../../utils/Constants";
import useCurrencySymbol from "../../../../hooks/useCurrencySymbol";
import { useTopPromoCodes } from "../../../../queries/affiliates/useAffiliateRankings";
import type { AffiliateRankedItem } from "../../../../queries/affiliates/useAffiliateRankings";
import type { AffiliateAnalyticsDataSource } from "../../../../queries/affiliates/analyticsDataSource";

import styles from "./TopFiveCodes.module.css";
import { getAdminNow } from "../../../../utils/dateService";

const CustomDatePicker = DatePickerJs as ComponentType<{
  dates: DateRange;
  setDates: (dates: DateRange) => void;
  getData: (queryString: string | null) => void;
  showQuickSelect?: boolean;
  showPresets?: boolean;
  startRange?: string;
}>;

const ExportToXlsxButton = ExportToXlsxButtonJs as ComponentType<{
  data: Record<string, unknown>[];
  filename: string;
}>;

type DateRange = [Dayjs, Dayjs];

// Metric keys mirror the 6 portfolio KPI tiles, same as TopFiveAffiliates.
type MetricKey =
  | "TOTAL_FTDS"
  | "TOTAL_USERS"
  | "FTD_RATE"
  | "TOTAL_OWED"
  | "TOTAL_REVENUE"
  | "AVERAGE_LTV";

type CodeRow = {
  name: string;
} & Record<MetricKey, number>;

// BE sends FTD_RATE as a 0–100 percentage; the chart's formatter multiplies
// by 100, so normalise it back to a ratio here. `label` is null for codes —
// fall back to the code string (entityId).
const rankedToRow = (item: AffiliateRankedItem): CodeRow => ({
  name: item.label ?? item.entityId,
  TOTAL_FTDS: item.metrics.TOTAL_FTDS ?? 0,
  TOTAL_USERS: item.metrics.TOTAL_USERS ?? 0,
  FTD_RATE: (item.metrics.FTD_RATE ?? 0) / 100,
  TOTAL_OWED: item.metrics.TOTAL_OWED ?? 0,
  TOTAL_REVENUE: item.metrics.TOTAL_REVENUE ?? 0,
  AVERAGE_LTV: item.metrics.AVERAGE_LTV ?? 0,
});

const PERCENT_METRICS = new Set<MetricKey>(["FTD_RATE"]);
const CURRENCY_METRICS = new Set<MetricKey>([
  "TOTAL_OWED",
  "TOTAL_REVENUE",
  "AVERAGE_LTV",
]);

export type TopFiveCodesProps = {
  affiliateId?: string;
  // BE UPPER_SNAKE keys. Undefined → full catalogue. See TopFiveAffiliates
  // for the same pattern.
  visibleMetrics?: ReadonlyArray<string>;
  dataSource?: AffiliateAnalyticsDataSource;
  // Cross-cut filters lifted from the KPIs tab's global filter dropdown.
  affiliate?: string;
  promoCode?: string;
  // False → render empty without fetching (previews of an affiliate that
  // doesn't exist yet).
  queryEnabled?: boolean;
};

const TopFiveCodes: FC<TopFiveCodesProps> = ({
  affiliateId,
  visibleMetrics,
  dataSource = "operator",
  affiliate,
  promoCode,
  queryEnabled = true,
}) => {
  const currencySymbol = useCurrencySymbol() ?? "$";
  const [dates, setDates] = useState<DateRange>(() => [
    getAdminNow().startOf("month"),
    getAdminNow().endOf("day"),
  ]);
  const [metric, setMetric] = useState<MetricKey>("TOTAL_FTDS");

  const query = useTopPromoCodes(
    {
      start_date: dates[0].format("YYYY-MM-DD"),
      end_date: dates[1].format("YYYY-MM-DD"),
    },
    metric,
    affiliateId,
    { affiliate, promo_code: promoCode },
    dataSource,
    { enabled: queryEnabled },
  );
  const rows = useMemo(
    () => (query.data ?? []).map(rankedToRow),
    [query.data],
  );

  // Built inside the memo: AppStrings values are reassigned when the
  // localized strings load, so a module-level const would freeze English.
  const metricOptions = useMemo(() => {
    const allMetricOptions: { value: MetricKey; label: string }[] = [
      { value: "TOTAL_FTDS", label: AppStrings.TOTAL_FTDS },
      { value: "TOTAL_USERS", label: AppStrings.TOTAL_USERS },
      { value: "FTD_RATE", label: AppStrings.FTD_RATE },
      { value: "TOTAL_OWED", label: AppStrings.TOTAL_OWED },
      { value: "TOTAL_REVENUE", label: AppStrings.TOTAL_REVENUE },
      { value: "AVERAGE_LTV", label: AppStrings.AVERAGE_LTV },
    ];
    return visibleMetrics
      ? allMetricOptions.filter((opt) => visibleMetrics.includes(opt.value))
      : allMetricOptions;
  }, [visibleMetrics]);

  useEffect(() => {
    if (metricOptions.length === 0) return;
    if (!metricOptions.some((opt) => opt.value === metric)) {
      setMetric(metricOptions[0].value);
    }
  }, [metricOptions, metric]);

  const formatValue = useCallback(
    (value: number) => {
      if (PERCENT_METRICS.has(metric)) return `${(value * 100).toFixed(1)}%`;
      if (CURRENCY_METRICS.has(metric)) {
        // Sign before symbol — matches the convention used in the KPI tiles.
        const sign = value < 0 ? "-" : "";
        const abs = Intl.NumberFormat("en-US").format(Math.abs(value));
        return `${sign}${currencySymbol}${abs}`;
      }
      return Intl.NumberFormat("en-US").format(value);
    },
    [metric, currencySymbol],
  );

  const getData = useCallback((_queryString: string | null) => {
    // Date changes flow through `dates` -> the query key.
  }, []);

  const exportRows = useMemo(
    () =>
      rows.map((r) => ({
        code: r.name,
        total_ftds: r.TOTAL_FTDS,
        total_users: r.TOTAL_USERS,
        ftd_rate: r.FTD_RATE * 100,
        total_owed: r.TOTAL_OWED,
        total_revenue: r.TOTAL_REVENUE,
        average_ltv: r.AVERAGE_LTV,
      })),
    [rows],
  );

  const chartData = useMemo(
    () => ({
      labels: rows.map((r) => r.name),
      datasets: [
        {
          data: rows.map((r) => r[metric]),
          backgroundColor: rows.map(
            (_, idx) => CHART_PALETTE.bars[idx % CHART_PALETTE.bars.length],
          ),
          borderRadius: 4,
          maxBarThickness: 22,
        },
      ],
    }),
    [rows, metric],
  );

  const chartOptions = useMemo(
    () => ({
      layout: { padding: { right: 56 } },
      scales: {
        x: { beginAtZero: true, grid: { display: true } },
        y: { grid: { display: false }, ticks: { font: { size: 12 } } },
      },
      plugins: {
        tooltip: {
          displayColors: false,
          callbacks: {
            label: (item: { raw: unknown }) => formatValue(Number(item.raw)),
          },
        },
        datalabels: {
          display: true,
          anchor: "end" as const,
          align: "end" as const,
          color: "#ffffff",
          font: { family: "Poppins-regular", size: 12, weight: 600 },
          formatter: (value: number) => formatValue(value),
        },
      },
    }),
    [formatValue],
  );

  return (
    <ChartCard
      title={AppStrings.TOP_5_CODES}
      headerExtra={
        <Select<MetricKey>
          className={styles.metricSelect}
          value={metric}
          options={metricOptions}
          onChange={(value) => setMetric(value as MetricKey)}
          aria-label={AppStrings.METRIC}
        />
      }
      bodyClassName={styles.chartContainer}
      controls={
        <>
          <CustomDatePicker
            getData={getData}
            dates={dates}
            setDates={setDates}
            showPresets
            startRange={DATE_RANGE.CURRENT_MONTH}
          />
          <ExportToXlsxButton
            data={exportRows}
            filename="affiliates-top-5-codes.xlsx"
          />
        </>
      }
    >
      <BarChart
        horizontal
        data={chartData}
        optionsOverride={chartOptions}
        plugins={[ChartDataLabels as never]}
      />
    </ChartCard>
  );
};

export default TopFiveCodes;
